import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { recordInteraction } from '../api';
import { useAuth } from '../auth/useAuth';
import { labelForPath } from '../lib/screenLabel';

/**
 * Returns true when the label looks like machine-generated text (file paths,
 * diff stats, timeline expanders) rather than a stable UI label. These are a
 * backstop for clicks that escape a [data-no-analytics] zone.
 */
export function isMachineLabel(label: string): boolean {
  // Timeline tool-group expanders (▸ / ▾ prefix)
  if (/^[▸▾]/.test(label)) return true;
  // Contains a path separator sequence — e.g. src/components/Foo.tsx
  if (/\/[\w.-]+\//.test(label)) return true;
  // Diff stat pattern: +N −N or +N -N
  if (/\+\d+\s*[−-]\d+/.test(label)) return true;
  return false;
}

/**
 * Auto-captures button and tab clicks via event delegation and records them
 * server-side for display in the Metrics page. Fire-and-forget — errors are
 * silently swallowed so tracking never disrupts the UI.
 *
 * Skips clicks inside [data-no-analytics] zones (file trees, diff viewers,
 * timeline tool-group expanders) and any label that looks like machine text.
 */
export function useInteractionTracking(): void {
  const { pathname } = useLocation();
  const { isAuthenticated, initializing } = useAuth();

  useEffect(() => {
    // Skip until auth is resolved — no token, no user ID to attach.
    if (initializing || !isAuthenticated) return;

    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const el = target.closest('button, [role="tab"]') as HTMLElement | null;
      if (!el) return;

      // Ignore clicks inside containers that render dynamic/machine-generated text.
      if (el.closest('[data-no-analytics]')) return;

      const rawLabel =
        el.getAttribute('aria-label') ||
        el.getAttribute('data-analytics') ||
        el.textContent?.trim();
      if (!rawLabel) return;

      // Backstop: drop labels that look like file paths, diff stats, or expanders.
      if (isMachineLabel(rawLabel)) return;

      const element = rawLabel.slice(0, 100);
      const screen = labelForPath(pathname);

      void recordInteraction(element, screen).catch(() => undefined);
    }

    document.addEventListener('click', handleClick, { capture: true });
    return () => document.removeEventListener('click', handleClick, { capture: true });
  }, [pathname, isAuthenticated, initializing]);
}
